import React, { useEffect, useState } from "react";
import {
  TextField,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogContentText,
  DialogActions,
} from "@mui/material";
import { signOut, updatePassword, fetchUserAttributes } from "aws-amplify/auth";
import { useNavigate } from "react-router-dom";
import { useAuth } from "../../context/AuthContext";
import PersonIcon from "@mui/icons-material/Person";
import { AppButton } from "../../components/ui";
import "./Account.scss";

const AccountPage = () => {
  const navigate = useNavigate();
  const auth = useAuth();

  // User email
  const [userEmail, setUserEmail] = useState("");

  // Password change state
  const [showPasswordForm, setShowPasswordForm] = useState(false);
  const [currentPassword, setCurrentPassword] = useState("");
  const [newPassword, setNewPassword] = useState("");
  const [confirmPassword, setConfirmPassword] = useState("");
  const [passwordError, setPasswordError] = useState("");
  const [passwordSuccess, setPasswordSuccess] = useState("");
  const [changingPassword, setChangingPassword] = useState(false);

  // Logout confirmation dialog
  const [logoutDialogOpen, setLogoutDialogOpen] = useState(false);

  // Scroll to top on mount
  useEffect(() => {
    window.scrollTo(0, 0);
  }, []);

  // Fetch user email
  useEffect(() => {
    const getUserEmail = async () => {
      try {
        const attributes = await fetchUserAttributes();
        setUserEmail(attributes.email || "");
      } catch (err) {
        console.error("Error fetching user attributes:", err);
      }
    };
    getUserEmail();
  }, []);

  const handleLogout = async () => {
    try {
      await signOut();
      console.log("User logged out successfully.");
      navigate("/login");
    } catch (err) {
      console.error("Error during logout:", err);
    }
  };

  const handlePasswordChange = async () => {
    setPasswordError("");
    setPasswordSuccess("");

    // Validation
    if (!currentPassword || !newPassword || !confirmPassword) {
      setPasswordError("Please fill in all fields.");
      return;
    }

    if (newPassword.length < 8) {
      setPasswordError("New password must be at least 8 characters.");
      return;
    }

    if (newPassword !== confirmPassword) {
      setPasswordError("New passwords do not match.");
      return;
    }

    setChangingPassword(true);

    try {
      await updatePassword({
        oldPassword: currentPassword,
        newPassword: newPassword,
      });
      setPasswordSuccess("Password changed successfully!");
      setCurrentPassword("");
      setNewPassword("");
      setConfirmPassword("");
      setTimeout(() => {
        setShowPasswordForm(false);
        setPasswordSuccess("");
      }, 2000);
    } catch (err) {
      console.error("Error changing password:", err);
      if (err.name === "NotAuthorizedException") {
        setPasswordError("Current password is incorrect.");
      } else if (err.name === "InvalidPasswordException") {
        setPasswordError("New password does not meet requirements.");
      } else {
        setPasswordError(err.message || "Failed to change password.");
      }
    } finally {
      setChangingPassword(false);
    }
  };

  const cancelPasswordChange = () => {
    setShowPasswordForm(false);
    setCurrentPassword("");
    setNewPassword("");
    setConfirmPassword("");
    setPasswordError("");
    setPasswordSuccess("");
  };

  return (
    <div className="accountPage">
      {/* Header */}
      <div className="accountHeader">
        <h1 className="accountTitle">Account</h1>
      </div>

      {/* Profile Section */}
      <div className="accountSection">
        <div className="accountProfile">
          <div className="accountAvatar">
            <PersonIcon className="avatarIcon" />
          </div>
          <div className="accountProfileInfo">
            <div className="accountName">{auth.organizationName || "User"}</div>
            <div className="accountEmail">{userEmail || "—"}</div>
          </div>
        </div>
      </div>

      {/* Security Section */}
      <div className="accountSection">
        <div className="accountSectionTitle">Security</div>
        <div className="accountSettingsList">
          <div className="accountSettingsItem">
            <div className="accountSettingsInfo">
              <div className="accountSettingsLabel">Password</div>
              <div className="accountSettingsDesc">
                {showPasswordForm ? "Enter your current and new password" : "Change your account password"}
              </div>
            </div>
            {!showPasswordForm && (
              <AppButton
                className="accountBtn"
                kind="secondary"
                onClick={() => setShowPasswordForm(true)}
              >
                Change Password
              </AppButton>
            )}
          </div>

          {showPasswordForm && (
            <div className="passwordForm">
              <TextField
                label="Current Password"
                type="password"
                value={currentPassword}
                onChange={(e) => setCurrentPassword(e.target.value)}
                fullWidth
                size="small"
              />
              <TextField
                label="New Password"
                type="password"
                value={newPassword}
                onChange={(e) => setNewPassword(e.target.value)}
                fullWidth
                size="small"
                helperText="Must be at least 8 characters"
              />
              <TextField
                label="Confirm New Password"
                type="password"
                value={confirmPassword}
                onChange={(e) => setConfirmPassword(e.target.value)}
                fullWidth
                size="small"
              />

              {passwordError && (
                <div className="accountMessage error">{passwordError}</div>
              )}
              {passwordSuccess && (
                <div className="accountMessage success">{passwordSuccess}</div>
              )}

              <div className="passwordFormActions">
                <AppButton
                  className="accountBtn"
                  kind="primary"
                  onClick={handlePasswordChange}
                  disabled={changingPassword}
                >
                  {changingPassword ? "Saving..." : "Save Password"}
                </AppButton>
                <AppButton
                  className="accountBtn"
                  kind="ghost"
                  onClick={cancelPasswordChange}
                  disabled={changingPassword}
                >
                  Cancel
                </AppButton>
              </div>
            </div>
          )}
        </div>
      </div>

      {/* Sign Out Section */}
      <div className="accountSection accountDangerZone">
        <div className="accountLogoutSection">
          <div className="accountLogoutInfo">
            <div className="accountLogoutTitle">Sign out</div>
            <div className="accountLogoutDesc">
              You will need to sign in again to access your dashboard
            </div>
          </div>
          <AppButton
            className="accountBtnDanger"
            kind="danger"
            onClick={() => setLogoutDialogOpen(true)}
          >
            Sign Out
          </AppButton>
        </div>
      </div>

      {/* Logout Confirmation Dialog */}
      <Dialog
        open={logoutDialogOpen}
        onClose={() => setLogoutDialogOpen(false)}
        PaperProps={{ className: "accountDialogPaper" }}
      >
        <DialogTitle>Sign Out</DialogTitle>
        <DialogContent>
          <DialogContentText>
            Are you sure you want to sign out of your account?
          </DialogContentText>
        </DialogContent>
        <DialogActions>
          <AppButton className="accountBtn" kind="ghost" onClick={() => setLogoutDialogOpen(false)}>
            Cancel
          </AppButton>
          <AppButton className="accountBtnDanger" kind="danger" onClick={handleLogout}>
            Sign Out
          </AppButton>
        </DialogActions>
      </Dialog>
    </div>
  );
};

export default AccountPage;
